import Icon from "@afa-shared/afa-components/dist/Icon";
import { useId, useRef, useState } from "react";

import { useClickOutside } from "hooks/useOutsideClick";
import { IFocusState, useFocusState } from "hooks/zustand/useFocusState";
import { EducationSearchItem } from "types/EducationProviderItem";

import educationSearchStyles from "./EducationSearch.module.css";

interface IEducationSearch {
  educationSearchList: Array<EducationSearchItem>;
  searchQuery: string;
  onSearch: (v?: string) => void;
}

const EducationSearch = ({ educationSearchList, searchQuery, onSearch }: IEducationSearch) => {
  const setOverridePageTopFocus = useFocusState(
    (state: IFocusState) => state.setOverridePageTopFocus,
  );
  const setOnPageLoadRef = useFocusState((state: IFocusState) => state.setOnPageLoadRef);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [suggestionsActive, setSuggestionsActive] = useState(false);
  const [value, setValue] = useState(searchQuery);
  const itemRefs = [];
  const searchWrapperRef = useRef(null);
  const searchInpuRef = useRef(null);
  const searchLabel = useId();

  useClickOutside(searchWrapperRef, () => {
    setSuggestions([]);
    setSuggestionsActive(false);
  });

  const handleChange = (event: any) => {
    const { value } = event.target;
    setValue(value);
    if (value?.toLowerCase().length > 1) {
      const filterSuggestions = educationSearchList?.filter(
        (suggestion) => suggestion?.name?.toLowerCase().indexOf(value.toLowerCase()) > -1,
      );
      setSuggestions(filterSuggestions);
      setSuggestionsActive(true);
    } else {
      setSuggestionsActive(false);
    }
  };

  const handleClick = (e) => {
    setSuggestions([]);
    setValue(e.target.innerText);
    setSuggestionsActive(false);
    onSearch(e.target.innerText);
  };

  const handleClear = () => {
    setSuggestions([]);
    setValue("");
    setSuggestionsActive(false);
    if (searchQuery) {
      onSearch("");
      setOverridePageTopFocus(true);
      setOnPageLoadRef(searchInpuRef);
    } else {
      searchInpuRef?.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (suggestions.length === 0) {
      return;
    }
    // UP ARROW
    if (e.keyCode === 38) {
      if (suggestionIndex === 0) {
        return;
      }
      setSuggestionIndex(suggestionIndex - 1);
      itemRefs[suggestionIndex - 1]?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    }
    // DOWN ARROW
    else if (e.keyCode === 40) {
      if (suggestionIndex === suggestions.length - 1) {
        return;
      }
      setSuggestionIndex(suggestionIndex + 1);
      itemRefs[suggestionIndex + 1].scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    }
    // ENTER
    else if (e.keyCode === 13) {
      setValue(suggestions[suggestionIndex]?.name);
      setSuggestionIndex(0);
      setSuggestionsActive(false);
      onSearch(suggestions[suggestionIndex]?.name);
    }
  };

  const handleItemHover = (index: number) => {
    setSuggestionIndex(index);
  };

  const handleFocus = () => {
    setSuggestionIndex(0);
  };

  return (
    <div className={educationSearchStyles.educationSearch} ref={searchWrapperRef}>
      <div className={educationSearchStyles.textInputWrapper}>
        <label
          className={educationSearchStyles.screenReaderWrapper}
          id={searchLabel}
          htmlFor={"education-search"}
        >
          Sök utbildningsanordnare
        </label>
        <div>
          {!value && (
            <Icon
              size="sm"
              name="search"
              viewBox="0 0 22 22"
              className={educationSearchStyles.searchIcon}
            />
          )}
          <input
            className={`${educationSearchStyles.textInput} ${
              value ? educationSearchStyles.hasValue : ""
            }`}
            name={"education-search"}
            id={"education-search"}
            value={value || ""}
            type="text"
            placeholder={"Sök efter namn eller region"}
            onChange={(event) => handleChange(event)}
            aria-labelledby={searchLabel}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onClick={(event) => handleChange(event)}
            ref={searchInpuRef}
            autoComplete="off"
          />
          {value && (
            <button
              className={educationSearchStyles.clearButton}
              onClick={() => handleClear()}
              aria-label="Rensa sökfält"
            >
              <Icon size="sm" name="close" />
            </button>
          )}
        </div>
      </div>
      {suggestionsActive && (
        <span className="notranslate" translate="no">
          <ul className={educationSearchStyles.list}>
            {suggestions.map((suggestion: EducationSearchItem, index: number) => {
              const string = suggestion?.name?.substr(
                0,
                suggestion?.name?.toLowerCase().indexOf(value.toLowerCase()),
              );
              const endString = suggestion?.name?.substr(
                suggestion?.name?.toLowerCase().indexOf(value.toLowerCase()) + value.length,
              );
              const highlightedText = suggestion?.name?.substr(
                suggestion?.name?.toLowerCase().indexOf(value.toLowerCase()),
                value.length,
              );
              return (
                <li
                  className={`${educationSearchStyles.listItem} ${
                    index === suggestionIndex ? educationSearchStyles.isActive : ""
                  }`}
                  key={index}
                  onClick={handleClick}
                  onMouseEnter={() => handleItemHover(index)}
                  ref={(li: any) => {
                    itemRefs[index] = li;
                  }}
                >
                  {string}
                  <span className={educationSearchStyles.span}>{highlightedText}</span>
                  {endString}
                </li>
              );
            })}
          </ul>
        </span>
      )}
    </div>
  );
};

export default EducationSearch;
